import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import { genderLabel } from '../util/localeHelpers';
import PortalTable, { PortalFieldDef } from '../util/PortalTable';

// ---------- Constants ----------

const TABLE = 'crm_contact';

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text', long_text: 'Long Text', number: 'Number', date: 'Date',
  date_range: 'Date Range', address: 'Address', image: 'Image', email: 'Email',
  phone: 'Phone', url: 'URL', select: 'Select', multi_select: 'Multi Select',
  checkbox: 'Checkbox', currency: 'Currency', file: 'File', user: 'User',
};

const SYSTEM_FIELDS = [
  { fieldKey: 'firstName',     label: 'First Name',    type: 'text',      isCore: true,  hasOptions: false },
  { fieldKey: 'lastName',      label: 'Last Name',     type: 'text',      isCore: false, hasOptions: false },
  { fieldKey: 'middleName',    label: 'Middle Name',   type: 'text',      isCore: false, hasOptions: false },
  { fieldKey: 'contactType',   label: 'Contact Type',  type: 'select',    isCore: true,  hasOptions: false },
  { fieldKey: 'organisationId',label: 'Organisation',  type: 'select',    isCore: false, hasOptions: false },
  { fieldKey: 'email',         label: 'Email',         type: 'email',     isCore: false, hasOptions: false },
  { fieldKey: 'phone1',        label: 'Mobile Phone',  type: 'phone',     isCore: false, hasOptions: false },
  { fieldKey: 'phone2',        label: 'Work Phone',    type: 'phone',     isCore: false, hasOptions: false },
  { fieldKey: 'jobTitle',      label: 'Job Title',     type: 'text',      isCore: false, hasOptions: false },
  { fieldKey: 'department',    label: 'Department',    type: 'text',      isCore: false, hasOptions: false },
  { fieldKey: 'gender',        label: 'Gender',        type: 'select',    isCore: false, hasOptions: false },
  { fieldKey: 'dateOfBirth',   label: 'Date of Birth', type: 'date',      isCore: false, hasOptions: false },
  { fieldKey: 'notes',         label: 'Description',   type: 'long_text', isCore: false, hasOptions: false },
];

const MODAL_STYLE = {
  position: 'absolute' as const, top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420, bgcolor: 'var(--bg-form)',
  border: 'var(--border-input)', borderRadius: '5px', boxShadow: 24, p: 3,
};

// ---------- TruncatedValues ----------

function TruncatedValues({ values }: { values: string[] }) {
  const display = values.join(', ');
  const truncated = display.length > 40 ? display.slice(0, 40) + '…' : display;
  if (!display) return <span className="opacity-40">—</span>;
  return (
    <Tooltip title={display} placement="top" arrow>
      <span style={{ cursor: 'default', whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis', maxWidth: 200, display: 'block' }}>
        {truncated}
      </span>
    </Tooltip>
  );
}

// ---------- AddFieldModal ----------

interface ReplaceItem { option: string; count: number; replacement: string | null; }

function AddFieldModal({ open, onClose, onSaved, editing, contactTypes }: {
  open: boolean; onClose: () => void; onSaved: () => Promise<void>; editing?: any; contactTypes: any[];
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [type, setType] = useState('text');
  const [selectedTypeIds, setSelectedTypeIds] = useState<Set<number>>(new Set());
  const [options, setOptions] = useState<string[]>([]);
  const [optionInput, setOptionInput] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isShown, setIsShown] = useState(true);
  const [step, setStep] = useState<'form' | 'replace'>('form');
  const [replaceItems, setReplaceItems] = useState<ReplaceItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.column_name ?? '');
      setType(editing.type ?? 'text');
      setOptions(Array.isArray(editing.options) ? editing.options : []);
      setIsRequired(editing.isRequired ?? false);
      setIsShown(editing.isShown ?? true);
    } else {
      setName(''); setType('text'); setOptions([]);
      setIsRequired(false); setIsShown(true);
    }
    setOptionInput('');
    setStep('form');
    setReplaceItems([]);
  }, [open, editing]);

  useEffect(() => {
    if (!open || contactTypes.length === 0) return;
    if (editing?.typeIds?.length > 0) {
      setSelectedTypeIds(new Set(editing.typeIds.map(Number)));
    } else {
      setSelectedTypeIds(new Set(contactTypes.map((ct: any) => ct.id)));
    }
  }, [open, editing, contactTypes]);

  const toggleType = (id: number) => {
    setSelectedTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const addOptions = () => {
    const val = optionInput.trim();
    if (!val) return;
    setOptions((prev) => prev.includes(val) ? prev : [...prev, val]);
    setOptionInput('');
  };

  const removeOption = (opt: string) => setOptions((prev) => prev.filter((o) => o !== opt));

  const buildPayload = () => {
    const allSelected = selectedTypeIds.size === contactTypes.length;
    return {
      table_name: TABLE,
      column_name: name.trim(),
      type,
      typeIds: allSelected ? null : Array.from(selectedTypeIds),
      options: ['select', 'multi_select'].includes(type) ? options : undefined,
      isRequired,
      isShown,
    };
  };

  const persistField = async () => {
    try {
      if (editing?.id) { await axios.put(`fields/${editing.id}`, buildPayload()); }
      else { await axios.post('fields/add', buildPayload()); }
      enqueueSnackbar(editing ? t('Change') : t('Add'), { variant: 'success' });
      await onSaved();
      onClose();
    } catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (!name.trim()) { enqueueSnackbar(t('Required'), { variant: 'error' }); return; }

    // Check for removed options only when editing an existing select/multi_select
    if (editing?.id && ['select', 'multi_select'].includes(type)) {
      const original: string[] = Array.isArray(editing.options) ? editing.options : [];
      const removed = original.filter((o) => !options.includes(o));
      if (removed.length > 0) {
        setSaving(true);
        try {
          const params = new URLSearchParams();
          removed.forEach((o) => params.append('options', o));
          const { data } = await axios.get(`fields/${editing.id}/option-usage?${params}`);
          const affected: ReplaceItem[] = removed
            .filter((o) => (data[o] ?? 0) > 0)
            .map((o) => ({ option: o, count: data[o], replacement: options[0] ?? null }));
          if (affected.length > 0) {
            setReplaceItems(affected);
            setStep('replace');
            setSaving(false);
            return;
          }
        } catch {
          // backend unavailable — proceed without check
        }
        setSaving(false);
      }
    }

    setSaving(true);
    await persistField();
  };

  const handleConfirmReplace = async () => {
    setSaving(true);
    try {
      for (const item of replaceItems) {
        await axios.post(`fields/${editing.id}/replace-option`, {
          oldValue: item.option,
          newValue: item.replacement,
        });
      }
      await persistField();
    } catch {
      enqueueSnackbar(t('Error'), { variant: 'error' });
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box sx={{ ...MODAL_STYLE, width: 460, maxHeight: '90vh', overflowY: 'auto' }}>

          {/* ── Form step ── */}
          {step === 'form' && (
            <>
              <div className="text-[15px] font-semibold mb-[14px] text-[var(--text-color)]">
                {editing ? t('Edit') : t('AddField')}
              </div>

              {/* Name */}
              <div className="flex flex-col mb-[10px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  {t('Name')}
                </label>
                <input title={t('Name')}
                  className="w-full text-[13px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                  value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              {/* Field type */}
              <div className="flex flex-col mb-[10px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  {t('FieldType')}
                </label>
                <select className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
                  value={type} onChange={(e) => setType(e.target.value)}>
                  {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              {/* Options */}
              {['select', 'multi_select'].includes(type) && (
                <div className="mb-[10px]">
                  <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[6px] text-[var(--text-color-calendar-week)] block">
                    Options
                  </label>
                  <div className="flex gap-[6px] mb-[8px]">
                    <input
                      className="flex-1 text-[13px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                      type="text"
                      placeholder="Add option..."
                      value={optionInput}
                      onChange={(e) => setOptionInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { addOptions(); e.preventDefault(); } }}
                    />
                    <button type="button" onClick={addOptions}
                      className="btn-primary">
                      Add
                    </button>
                  </div>
                  {options.length > 0 && (
                    <div className="flex flex-wrap gap-[5px]">
                      {options.map((opt) => (
                        <span key={opt}
                          className="inline-flex items-center gap-[4px] text-[12px] px-[9px] py-[2px] rounded-full font-medium"
                          style={{ backgroundColor: '#3b82f6', color: '#fff' }}>
                          {opt}
                          <button type="button" title={`Remove "${opt}"`} onClick={() => removeOption(opt)}
                            className="flex items-center justify-center w-[13px] h-[13px] rounded-full hover:bg-white/20 leading-none transition-colors">
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Scope */}
              {contactTypes.length > 0 && (
                <div className="mb-[10px]">
                  <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[6px] text-[var(--text-color-calendar-week)] block">
                    Scope ({t('ContactType')})
                  </label>
                  <div className="flex flex-wrap gap-[6px]">
                    {contactTypes.map((ct: any) => {
                      const active = selectedTypeIds.has(ct.id);
                      return (
                        <button key={ct.id} type="button" onClick={() => toggleType(ct.id)}
                          className="text-[12px] px-[10px] py-[3px] rounded-full border font-medium transition-all select-none"
                          style={{
                            backgroundColor: active ? '#3b82f6' : 'var(--btn-user-form)',
                            borderColor: active ? '#3b82f6' : 'var(--border-color)',
                            color: active ? '#fff' : 'var(--text-color-calendar-week)',
                          }}>
                          {ct.name}
                        </button>
                      );
                    })}
                  </div>
                  {selectedTypeIds.size === 0 && (
                    <div className="text-[11px] text-orange-400 mt-[5px]">
                      ⚠ No types selected — field will be hidden everywhere
                    </div>
                  )}
                </div>
              )}

              {/* Flags */}
              <div className="flex items-center gap-[16px] mb-[14px] text-[13px] text-[var(--text-color)]">
                <label className={`flex items-center gap-[6px] ${isShown ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                  <input type="checkbox" title={t('Required')} disabled={!isShown} checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)} />
                  {t('Required')}
                </label>
                <label className="flex items-center gap-[6px] cursor-pointer">
                  <input type="checkbox" title={t('Visible')} checked={isShown} onChange={(e) => {
                    const v = e.target.checked;
                    setIsShown(v);
                    if (!v) setIsRequired(false);
                  }} />
                  {t('Visible')}
                </label>
              </div>

              <div className="flex gap-[8px]">
                <Button size="small" variant="contained" sx={{ flex: 1, fontSize: 12 }}
                  disabled={saving} onClick={handleSave}>
                  {saving ? '...' : editing ? t('Change') : t('Add')}
                </Button>
                <Button size="small" variant="outlined" sx={{ flex: 1, fontSize: 12 }} onClick={onClose}>
                  {t('Cancel')}
                </Button>
              </div>
            </>
          )}

          {/* ── Replace step ── */}
          {step === 'replace' && (
            <>
              <div className="text-[15px] font-semibold mb-[4px] text-[var(--text-color)]">
                Removed options are in use
              </div>
              <div className="text-[12px] text-[var(--text-color-calendar-week)] mb-[16px]">
                The options below are assigned to contacts. Choose a replacement or select "Clear" to remove the value.
              </div>

              <div className="flex flex-col gap-[10px] mb-[20px]">
                {replaceItems.map((item) => (
                  <div key={item.option}
                    className="rounded-[6px] border border-[var(--border-input)] bg-[var(--btn-user-form)] p-[12px]">
                    <div className="flex items-center justify-between mb-[8px]">
                      <span className="inline-flex items-center text-[12px] px-[9px] py-[2px] rounded-full font-medium"
                        style={{ backgroundColor: '#ef4444', color: '#fff' }}>
                        {item.option}
                      </span>
                      <span className="text-[11px] text-[var(--text-color-calendar-week)]">
                        {item.count} record{item.count !== 1 ? 's' : ''} affected
                      </span>
                    </div>
                    <label className="text-[10px] text-[var(--text-color-calendar-week)] mb-[4px] block uppercase tracking-wider">
                      Replace with
                    </label>
                    <select
                      className="edit-select p-[5px] rounded-[5px] edit-input text-[13px] w-full"
                      value={item.replacement ?? '__clear__'}
                      onChange={(e) => setReplaceItems((prev) =>
                        prev.map((r) => r.option === item.option
                          ? { ...r, replacement: e.target.value === '__clear__' ? null : e.target.value }
                          : r)
                      )}>
                      <option value="__clear__">— Clear value —</option>
                      {options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              <div className="flex gap-[8px]">
                <Button size="small" variant="contained" sx={{ flex: 1, fontSize: 12 }}
                  disabled={saving} onClick={handleConfirmReplace}>
                  {saving ? '...' : 'Apply & Save'}
                </Button>
                <Button size="small" variant="outlined" sx={{ flex: 1, fontSize: 12 }}
                  onClick={() => setStep('form')}>
                  Back
                </Button>
              </div>
            </>
          )}

        </Box>
      </Fade>
    </Modal>
  );
}

// ---------- Main component ----------

export default function CrmContactFields() {
  const { enqueueSnackbar } = useSnackbar();
  const [fieldConfigs, setFieldConfigs] = useState<any[]>([]);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [contactTypes, setContactTypes] = useState<any[]>([]);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [editingField, setEditingField] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const [{ data: configs }, { data: fields }, { data: gens }, { data: types }] =
        await Promise.all([
          axios.get(`crm/field-config?table=${TABLE}`),
          axios.get(`fields/custom?table=${TABLE}`),
          axios.get('crm/genders'),
          axios.get('crm/contact-types'),
        ]);
      setFieldConfigs(Array.isArray(configs) ? configs : []);
      setCustomFields(Array.isArray(fields) ? fields : []);
      setGenders(Array.isArray(gens) ? gens : []);
      setContactTypes(Array.isArray(types) ? types : []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const getConfig = (fieldKey: string) =>
    fieldConfigs.find((c) => c.fieldKey === fieldKey && c.typeId === null);

  const handleSystemToggle = async (fieldKey: string, prop: 'isVisible' | 'isRequired', current: boolean) => {
    const cfg = getConfig(fieldKey);
    let isVisible = prop === 'isVisible' ? !current : (cfg?.isVisible ?? true);
    let isRequired = prop === 'isRequired' ? !current : (cfg?.isRequired ?? false);
    // Turning visibility off forces required off too
    if (prop === 'isVisible' && !isVisible) isRequired = false;
    const payload = { tableName: TABLE, fieldKey, typeId: null, isVisible, isRequired };
    try { await axios.post('crm/field-config', payload); load(); }
    catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const handleCustomToggle = async (field: any, prop: 'isShown' | 'isRequired', current: boolean) => {
    const updates: Record<string, boolean> = { [prop]: !current };
    // Turning visibility off forces required off too
    if (prop === 'isShown' && current) updates.isRequired = false;
    try { await axios.put(`fields/${field.id}`, updates); load(); }
    catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const handleDeleteField = async (id: number) => {
    try { await axios.delete(`fields/${id}`); load(); }
    catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const systemRows = SYSTEM_FIELDS.map((sf) => {
    const cfg = getConfig(sf.fieldKey);
    return {
      fieldKey: sf.fieldKey,
      label: sf.label,
      isCore: sf.isCore,
      isVisible: cfg ? cfg.isVisible : true,
      isRequired: cfg ? cfg.isRequired : false,
    };
  });

  const systemFields: PortalFieldDef[] = [
    {
      key: 'label', label: t('Name'), type: 'text', editable: false,
      render: (row) => (
        <span>
          {row.label}
          {row.isCore && <span className="text-[10px] opacity-30 ml-[5px]">core</span>}
        </span>
      ),
    },
    {
      key: 'values', label: t('Values'), type: 'text', width: 220, editable: false,
      render: (row) => {
        if (row.fieldKey === 'gender')
          return <TruncatedValues values={genders.map((g: any) => genderLabel(g))} />;
        return <span className="opacity-30">—</span>;
      },
    },
    {
      key: 'isRequired', label: t('Required'), type: 'boolean', variant: 'toggle',
      width: 80, align: 'right', editable: false,
      isDisabled: (row) => row.isCore || !row.isVisible,
    },
    {
      key: 'isVisible', label: t('Visible'), type: 'boolean', variant: 'toggle',
      width: 80, align: 'right', editable: false,
      isDisabled: (row) => row.isCore,
    },
  ];

  const customFieldDefs: PortalFieldDef[] = [
    {
      key: 'column_name', label: t('Name'), type: 'text', editable: false,
    },
    {
      key: 'values', label: t('Values'), type: 'text', width: 220, editable: false,
      render: (f) => {
        if (f.type === 'checkbox') return <span className="opacity-60 text-[12px]">Yes / No</span>;
        if (['select', 'multi_select'].includes(f.type) && Array.isArray(f.options))
          return <TruncatedValues values={f.options} />;
        return <span className="opacity-30">—</span>;
      },
    },
    {
      key: 'isRequired', label: t('Required'), type: 'boolean', variant: 'toggle',
      width: 80, align: 'right', editable: false,
      isDisabled: (row) => !row.isShown,
    },
    {
      key: 'isShown', label: t('Visible'), type: 'boolean', variant: 'toggle',
      width: 80, align: 'right', editable: false,
    },
  ];

  return (
    <>
      <PortalTable
        title={t('ContactFields')}
        rows={systemRows}
        getKey={(row) => row.fieldKey}
        fields={systemFields}
        onToggleField={(row, fieldKey) =>
          handleSystemToggle(row.fieldKey, fieldKey as 'isVisible' | 'isRequired', (row as any)[fieldKey])
        }
      />

      <PortalTable
        title={t('CustomFields')}
        rows={customFields}
        getKey={(f) => f.id}
        fields={customFieldDefs}
        onAdd={() => { setEditingField(null); setAddFieldOpen(true); }}
        addLabel={`${t('Add')} ${t('Field')}`}
        onEdit={(f) => { setEditingField(f); setAddFieldOpen(true); }}
        onDelete={(f) => handleDeleteField(f.id)}
        onDeleteMany={async (ids) => {
          await Promise.all(ids.map((id) => axios.delete(`fields/${id}`)));
          load();
        }}
        onToggleField={(f, fieldKey) =>
          handleCustomToggle(f, fieldKey as 'isShown' | 'isRequired', (f as any)[fieldKey])
        }
      />

      <AddFieldModal open={addFieldOpen} onClose={() => setAddFieldOpen(false)}
        onSaved={load} editing={editingField} contactTypes={contactTypes} />
    </>
  );
}
